import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import AdSlot from "../components/AdSlot";
import useSiteContact, { whatsappLink } from "../hooks/useSiteContact";

export default function Contact() {
  const contact = useSiteContact();
  const waLink = whatsappLink(contact);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    phone: "",
    email: "",
    city: "",
    services: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const toggleService = (title) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(title)
        ? f.services.filter((s) => s !== title)
        : [...f.services, title],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business_name || !form.phone) {
      toast.error("Please fill in name, business name and phone");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/inquiries", payload);
      setSuccess(true);
      toast.success("Thank you! We'll reach out within 24 hours.");
      setForm({ name: "", business_name: "", phone: "", email: "", city: "", services: [], message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact — Get a Free Restaurant Audit"
        description="Tell us about your restaurant and get a free Zomato & Swiggy audit within 24 hours. Call +91-9811378249 or +91-7060111949."
        path="/contact"
      />
      <section className="border-b border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-sm uppercase tracking-[0.25em] text-[#525252] mb-4">Get in touch</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter leading-none font-black max-w-3xl">
            Tell us about your <span className="text-[var(--brand-primary)]">restaurant</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#525252]">
            Fill the form and we'll get back to you with a free audit within 24 hours. No obligations.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* form */}
          <div className="lg:col-span-2">
            {success ? (
              <div data-testid="contact-success" className="bg-white border border-black/5 p-10 text-center">
                <CheckCircle2 size={48} className="text-[var(--brand-primary)] mx-auto" />
                <h2 className="font-display text-3xl font-bold mt-4">Inquiry received</h2>
                <p className="mt-2 text-[#525252]">Our team will reach out within 24 hours.</p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-6 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm hover:bg-black"
                  data-testid="contact-submit-another"
                >
                  Submit another inquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white border border-black/5 p-8 lg:p-10 space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name *" testId="contact-name">
                    <input
                      data-testid="contact-input-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                      required
                    />
                  </Field>
                  <Field label="Restaurant / Hotel name *" testId="contact-business">
                    <input
                      data-testid="contact-input-business"
                      type="text"
                      value={form.business_name}
                      onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                      className={inputCls}
                      required
                    />
                  </Field>
                  <Field label="Phone *" testId="contact-phone">
                    <input
                      data-testid="contact-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={inputCls}
                      required
                    />
                  </Field>
                  <Field label="Email" testId="contact-email">
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="City" testId="contact-city">
                    <input
                      data-testid="contact-input-city"
                      type="text"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-3">Services you're interested in</label>
                  <div className="flex flex-wrap gap-2">
                    {services.map((s) => {
                      const active = form.services.includes(s.title);
                      return (
                        <button
                          type="button"
                          key={s.id}
                          data-testid={`contact-service-${s.slug}`}
                          onClick={() => toggleService(s.title)}
                          className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
                            active
                              ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                              : "bg-white text-[#0A0A0A] border-black/10 hover:border-black/30"
                          }`}
                        >
                          {s.title}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Field label="Tell us more (optional)" testId="contact-message">
                  <textarea
                    data-testid="contact-input-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={inputCls}
                    placeholder="Share your Zomato/Swiggy link or anything you'd like us to know..."
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] disabled:opacity-60 px-8 py-4 rounded-sm transition-colors font-medium"
                >
                  {submitting ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            )}
          </div>

          {/* side */}
          <aside className="space-y-6">
            <div className="bg-[#0A0A0A] text-white p-8">
              <div className="text-sm uppercase tracking-[0.2em] text-white/40">Talk to us directly</div>
              <div className="mt-6 space-y-4">
                {(contact.phones || []).map((p, i) => (
                  <a key={p} href={`tel:${p}`}
                     data-testid={`contact-phone-link${i === 0 ? "" : `-${i + 1}`}`}
                     className="flex items-center gap-3 hover:text-[var(--brand-primary)]">
                    <Phone size={18}/> {p}
                  </a>
                ))}
                {contact.email && (
                  <a href={`mailto:${contact.email}`} data-testid="contact-email-link"
                     className="flex items-center gap-3 hover:text-[var(--brand-primary)]">
                    <Mail size={18}/> {contact.email}
                  </a>
                )}
                {waLink && (
                  <a href={waLink} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"
                     className="flex items-center gap-3 hover:text-[var(--brand-primary)]">
                    <MessageCircle size={18}/> WhatsApp us
                  </a>
                )}
              </div>
            </div>
            <div className="bg-white border border-black/5 p-8">
              <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Why us?</div>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--brand-primary)] shrink-0" /> Specialists in Zomato & Swiggy</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--brand-primary)] shrink-0" /> 48-hour turnaround</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--brand-primary)] shrink-0" /> Results-driven approach</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--brand-primary)] shrink-0" /> No long contracts</li>
              </ul>
            </div>

            {/* Sidebar ad slot */}
            <AdSlot page="contact" slot="sidebar" />
          </aside>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-black/10 rounded-sm px-4 py-3 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none transition-all";

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={`field-${testId}`}>
      <span className="block text-sm font-medium text-[#0A0A0A] mb-2">{label}</span>
      {children}
    </label>
  );
}
